import {
  HIGHER_RISK_ACTIVITIES,
  INTAKE_TIERS,
  LINK_TEMPLATES,
  linkTemplate,
  type LinkTemplate,
  INDUSTRIES,
  REVENUE_LABEL,
  TIER_LABEL,
  TRIAGE_FACTS,
  type Condition,
  type IntakeDryRunResult,
  type IntakeLink,
  type IntakeSubmission,
  type IntakeTier,
  type SubmissionStatus,
  type TierRule,
  type TriageFact,
} from '@cyberscore/contracts';
import { ArrowDown, ArrowUp, Copy, Globe, Inbox, Link2, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { hashQuery, href, navigate, useHash } from '../../lib/router';
import { useSession, can } from '../../lib/session';
import { Drawer } from '../../ui/Overlay';
import { useToast } from '../../ui/Toasts';
import { when } from '../clients/parts';
import {
  dryRun,
  useCheckFirstView,
  useDecide,
  useFirstView,
  useIntakeLinkChanges,
  useIntakeLinks,
  useSubmissions,
} from './api';

const STATUS: Record<SubmissionStatus, [string, string]> = { new: ['New', 'warn'], accepted: ['Accepted', 'ok'], rejected: ['Rejected', 'neutral'] }; // prettier-ignore
const tierShort = (t: IntakeTier) => TIER_LABEL[t].replace(/ \(.*\)$/, '');
export const publicUrl = (token: string) =>
  `${window.location.origin}${window.location.pathname}#/apply/${token}`;

/**
 * Submissions (task 68): what businesses sent through the insurer's or broker's links, to accept into a
 * client and an assessment or reject with a note; and the links themselves, with their tier rules.
 */
export function SubmissionsPage() {
  const hash = useHash();
  const tab = hashQuery(hash).get('tab') === 'links' ? 'links' : 'inbox';
  return (
    <>
      <div className="page-head">
        <h1>Submissions</h1>
        <p>Businesses that asked for cover through your links. Accept one to set up its client record and cyber check, and invite its owner.</p>
      </div>
      <div className="seg list-tabs" role="group" aria-label="Show">
        <button type="button" aria-pressed={tab === 'inbox'} onClick={() => navigate(href.submissions())}><Inbox size={14} /> Inbox</button>
        <button type="button" aria-pressed={tab === 'links'} onClick={() => navigate(href.submissions('?tab=links'))}><Link2 size={14} /> Links</button>
      </div>
      {tab === 'inbox' ? <InboxView /> : <LinksView />}
    </>
  ); // prettier-ignore
}

// —— the inbox ——

function InboxView() {
  const [status, setStatus] = useState<SubmissionStatus | ''>('new');
  const subs = useSubmissions(status);
  const [open, setOpen] = useState<IntakeSubmission | null>(null);
  return (
    <section className="card">
      <div className="dv-bar">
        <select className="input dv-filter" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as SubmissionStatus | '')}>
          <option value="new">New</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="">All</option>
        </select>
      </div>
      {subs.isPending ? <div className="empty">Loading…</div> : !subs.data?.length ? (
        <div className="empty"><Inbox size={28} /><strong>Nothing here</strong>Share a link from the Links tab; what businesses send shows up here.</div>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Business</th><th className="wide">Contact</th><th>Check</th><th className="wide">Link</th><th>Status</th><th className="wide">Received</th></tr></thead>
            <tbody>
              {subs.data.map((s) => (
                <tr key={s.id} className="clickable" onClick={() => setOpen(s)}>
                  <td><button type="button" className="linklike cell-title" onClick={() => setOpen(s)}>{s.answers.business_name}</button><div className="muted small">{INDUSTRIES[s.answers.industry]} · {s.answers.employees} staff</div></td>
                  <td className="wide small">{s.answers.contact_name}<div className="muted">{s.answers.contact_email}</div></td>
                  <td className="small">{tierShort(s.tier)}{s.rule && <div className="muted">{s.rule}</div>}</td>
                  <td className="wide small">{s.link.name}</td>
                  <td><span className={`pill ${STATUS[s.status][1]}`}>{STATUS[s.status][0]}</span></td>
                  <td className="wide muted small">{when(s.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <SubmissionDrawer s={open} onClose={() => setOpen(null)} />}
    </section>
  ); // prettier-ignore
}

function SubmissionDrawer({ s, onClose }: { s: IntakeSubmission; onClose: () => void }) {
  const { tenant } = useSession();
  const canDecide = can(tenant, 'intake.decide');
  const { accept, reject } = useDecide();
  const toast = useToast();
  const [tier, setTier] = useState<IntakeTier>(s.tier);
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState('');
  const [banner, setBanner] = useState<string | null>(null);
  const a = s.answers;
  const busy = accept.isPending || reject.isPending;

  const doAccept = async () => {
    setBanner(null);
    try {
      const done = await accept.mutateAsync({ id: s.id, ...(tier !== s.tier ? { tier } : {}) });
      toast(`${a.business_name} accepted. ${a.contact_email} has been sent the check.`);
      onClose();
      if (done.assessment_id) navigate(href.assessment(done.assessment_id));
    } catch (e) {
      setBanner(e instanceof ApiError ? e.message : 'Could not accept it');
    }
  };
  const doReject = async () => {
    setBanner(null);
    try {
      await reject.mutateAsync({ id: s.id, note: note.trim() });
      toast(`${a.business_name} rejected; they have been emailed your note.`);
      onClose();
    } catch (e) {
      setBanner(e instanceof ApiError ? e.message : 'Could not reject it');
    }
  };

  const facts: [string, ReactNode][] = [
    ['Industry', INDUSTRIES[a.industry]],
    ['Country', a.country],
    ['People', a.employees],
    [
      'Yearly revenue',
      a.revenue_amount != null ? `RM ${a.revenue_amount.toLocaleString('en-MY')}` : REVENUE_LABEL[a.revenue],
    ],
    ...(a.overseas_revenue
      ? [
          ['Revenue from outside Malaysia', a.overseas_revenue === 'yes' ? 'Yes' : 'No'] as [
            string,
            ReactNode,
          ],
        ]
      : []),
    ...(a.activities?.length
      ? [
          ['Higher-risk activities', a.activities.map((x) => HIGHER_RISK_ACTIVITIES[x]).join('; ')] as [
            string,
            ReactNode,
          ],
        ]
      : []),
    ['Card payments', a.card_payments === 'yes' ? 'Yes' : 'No'],
    ['Customers’ personal data', a.personal_data === 'yes' ? 'Yes' : 'No'],
    ['Website', a.website ?? '–'],
    ['Sum insured wanted', a.sum_insured != null ? `RM ${a.sum_insured.toLocaleString('en-MY')}` : '–'],
    ['Renews on', a.renewal_date ?? '–'],
    [
      'Contact',
      <>
        {a.contact_name}
        <br />
        <span className="muted">
          {a.contact_email}
          {a.phone ? ` · ${a.phone}` : ''}
        </span>
      </>,
    ],
  ];
  return (
    <Drawer
      title={a.business_name}
      subtitle={`${STATUS[s.status][0]} · from ${s.link.name} · ${when(s.created_at)}`}
      onClose={onClose}
      footer={
        s.status === 'new' && canDecide ? (
          rejecting ? (
            <><span className="grow" /><button type="button" className="btn" onClick={() => setRejecting(false)}>Back</button><button type="button" className="btn btn-danger" disabled={!note.trim() || busy} onClick={doReject}>Reject and email the note</button></>
          ) : (
            <><button type="button" className="btn" onClick={() => setRejecting(true)}>Reject…</button><span className="grow" /><button type="button" className="btn btn-primary" disabled={busy} onClick={doAccept}>{accept.isPending && <Loader2 size={15} className="spin" />}Accept and invite</button></>
          )
        ) : (
          <><span className="grow" />{s.assessment_id && <a className="btn" href={href.assessment(s.assessment_id)}>Open the assessment</a>}<button type="button" className="btn" onClick={onClose}>Close</button></>
        )
      }
    >
      {banner && <div className="result bad" role="alert"><span>{banner}</span></div>}
      <dl className="facts">
        {facts.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
      </dl>
      {a.notes && <p className="quote small">{a.notes}</p>}
      {s.internet_check ? <FirstLook s={s} canCheck={s.status === 'new' && canDecide} /> : a.website ? <p className="muted small">The business didn’t agree to an internet check of its website on the form.</p> : null}
      {s.status === 'new' && canDecide && !rejecting && (
        <div className="field">
          <label htmlFor="sub-tier">Check</label>
          <select id="sub-tier" className="input" value={tier} onChange={(e) => setTier(e.target.value as IntakeTier)}>
            {INTAKE_TIERS.map((t) => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}
          </select>
          <span className="hint">{s.rule ? `Picked by the rule “${s.rule}”.` : 'No rule matched: the link’s default.'} Accepting adds a client record, starts this check, and invites {a.contact_email}.</span>
        </div>
      )}
      {rejecting && (
        <div className="field">
          <label htmlFor="sub-note">Note to the business</label>
          <textarea id="sub-note" className="input" rows={4} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. We don’t cover businesses outside Malaysia yet." />
          <span className="hint">Emailed to {a.contact_email}.</span>
        </div>
      )}
      {s.status !== 'new' && (
        <p className="muted small">{STATUS[s.status][0]} by {s.decided_by_name ?? 'someone'}{s.decided_at ? `, ${when(s.decided_at)}` : ''}.{s.note ? ` Note: ${s.note}` : ''}{s.customer_key ? ` Client ${s.customer_key}, ${tierShort(s.tier).toLowerCase()}.` : ''}</p>
      )}
    </Drawer>
  ); // prettier-ignore
}

const GRADE_TONE: Record<string, string> = { A: 'ok', B: 'ok', C: 'warn', D: 'warn', F: 'bad' };

/**
 * A first look at the business's domain before it answers (task 73, decision 0053): only because it agreed
 * on the form, and provisional. Accepting carries it, and the consent, to the assessment.
 */
function FirstLook({ s, canCheck }: { s: IntakeSubmission; canCheck: boolean }) {
  const q = useFirstView(s.id, true);
  const check = useCheckFirstView(s.id);
  const toast = useToast();
  const v = q.data?.view;
  return (
    <section className="first-look" aria-labelledby={`fl-${s.id}`}>
      <div className="row-between">
        <h3 id={`fl-${s.id}`} className="row gap-s"><Globe size={15} aria-hidden /> First look at {s.internet_check!.domains.join(', ')}</h3>
        {v?.grade && <span className={`pill ${GRADE_TONE[v.grade]}`}>Grade {v.grade} · {v.score}</span>}
      </div>
      <p className="muted small">Provisional: what the domain shows from the internet before the business answers. It agreed to this on the form: “{s.internet_check!.consent_text}”</p>
      {v && (
        <ul className="oi-factors">
          {v.factors.filter((f) => f.score !== null).map((f) => (
            <li key={f.key}><span className={`pill ${GRADE_TONE[f.grade!]}`}>{f.grade}</span> <b>{f.name}</b>{f.issues.map((i) => <div key={i} className="muted small">{i}</div>)}</li>
          ))}
        </ul>
      )}
      {q.data?.run && <p className="muted tiny">{q.data.run.source_name} · {when(q.data.run.created_at)}</p>}
      {canCheck && (
        <button type="button" className="btn btn-sm" disabled={check.isPending} onClick={() => check.mutate(undefined, { onError: (e) => toast(e.message, 'bad') })}>
          {check.isPending && <Loader2 size={14} className="spin" />}{q.data?.run ? 'Check again' : `Check ${s.internet_check!.domains[0]}`}
        </button>
      )}
    </section>
  ); // prettier-ignore
}

// —— the links ——

function LinksView() {
  const links = useIntakeLinks();
  const { tenant } = useSession();
  const canEdit = can(tenant, 'intake.create', 'intake.edit');
  const toast = useToast();
  const [editing, setEditing] = useState<IntakeLink | 'new' | null>(null);
  const copy = async (l: IntakeLink) => {
    try {
      await navigator.clipboard.writeText(publicUrl(l.token));
      toast('Link copied.');
    } catch {
      toast(publicUrl(l.token));
    }
  };
  return (
    <section className="card">
      <div className="dv-bar">
        <span className="muted small grow">Each link opens a short form in your name. The rules pick how deep each business’s check goes.</span>
        {canEdit && <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}><Plus size={15} /> New link</button>}
      </div>
      {links.isPending ? <div className="empty">Loading…</div> : !links.data?.length ? (
        <div className="empty"><Link2 size={28} /><strong>No links yet</strong>Make one, then share it on your website or in emails.</div>
      ) : (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Link</th><th className="wide">Shown as</th><th>Rules</th><th className="right">New</th><th className="right wide">All</th><th /></tr></thead>
            <tbody>
              {links.data.map((l) => (
                <tr key={l.id}>
                  <td><b>{l.name}</b>{!l.active && <span className="pill neutral"> Off</span>}<div className="muted small mono">#/apply/{l.token.slice(0, 6)}…</div></td>
                  <td className="wide small"><span className="swatch" style={{ background: l.accent }} /> {l.brand_name}</td>
                  <td className="small">{l.rules.length} · default {tierShort(l.default_tier).toLowerCase()}</td>
                  <td className="right num">{l.submissions.new}</td>
                  <td className="right num wide">{l.submissions.total}</td>
                  <td className="right nowrap">
                    <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label={`Copy the link ${l.name}`} onClick={() => copy(l)}><Copy size={14} /></button>
                    <a className="btn btn-ghost btn-icon btn-sm" aria-label={`Open the form ${l.name}`} href={`#/apply/${l.token}`} target="_blank" rel="noreferrer"><Link2 size={14} /></a>
                    {canEdit && <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label={`Edit ${l.name}`} onClick={() => setEditing(l)}><Pencil size={14} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <LinkDrawer link={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </section>
  ); // prettier-ignore
}

/** One test in a rule: a fact, how to compare, and the values. Rules are "all of" these. */
type Test = { fact: TriageFact; op: 'in' | 'not_in' | 'gte' | 'lt'; value: string[] | string };
const testsOf = (c: Condition): Test[] =>
  ('all' in c ? c.all : [c]).flatMap((x) => ('fact' in x && x.fact in TRIAGE_FACTS ? [{ fact: x.fact as TriageFact, op: x.op as Test['op'], value: Array.isArray(x.value) ? x.value.map(String) : String(x.value ?? '') }] : [])); // prettier-ignore
const conditionOf = (tests: Test[]): Condition => {
  const leaves = tests.map((t) => ({ fact: t.fact, op: t.op, value: Array.isArray(t.value) ? t.value : Number(t.value) })) as Condition[]; // prettier-ignore
  return leaves.length === 1 ? leaves[0]! : { all: leaves };
};
const newTest = (fact: TriageFact = 'employees'): Test =>
  TRIAGE_FACTS[fact].options ? { fact, op: 'in', value: [] } : { fact, op: 'gte', value: '' };

function LinkDrawer({ link, onClose }: { link: IntakeLink | null; onClose: () => void }) {
  const { tenant } = useSession();
  const { create, update } = useIntakeLinkChanges();
  const toast = useToast();
  const [name, setName] = useState(link?.name ?? 'Website');
  // A new link starts from the recommended template (task 112); an existing one from what it is.
  const start = linkTemplate('standard_sme');
  const [template, setTemplate] = useState<LinkTemplate | null>(link ? null : 'standard_sme');
  const [heading, setHeading] = useState(link?.heading ?? start.heading);
  const [intro, setIntro] = useState(
    link?.intro ?? 'It takes two minutes. We will email you about the next step.',
  );
  const [brand, setBrand] = useState(link?.brand_name ?? tenant?.name ?? '');
  const [accent, setAccent] = useState(link?.accent ?? '#0f5c4c');
  const [active, setActive] = useState(link?.active ?? true);
  const [placement, setPlacement] = useState(link?.placement ?? start.placement);
  const [def, setDef] = useState<IntakeTier>(link?.default_tier ?? start.default_tier);
  const [journeys, setJourneys] = useState<Partial<Record<IntakeTier, string>>>(Object.fromEntries(Object.entries(link?.journeys ?? {}).map(([k, v]) => [k, v ?? '']))); // prettier-ignore
  const [rules, setRules] = useState<{ name: string; tier: IntakeTier; tests: Test[] }[]>((link?.rules ?? start.rules).map((r) => ({ name: r.name, tier: r.tier, tests: testsOf(r.when) }))); // prettier-ignore
  // Folded while a template's rules are as it made them; open to customise, or when there are none.
  const [rulesOpen, setRulesOpen] = useState(!!link);
  const applyTemplate = (t: LinkTemplate) => {
    const x = linkTemplate(t);
    setTemplate(t);
    if (!x.rules.length) setRulesOpen(true);
    setHeading(x.heading);
    setPlacement(x.placement);
    setDef(x.default_tier);
    setRules(x.rules.map((r) => ({ name: r.name, tier: r.tier, tests: testsOf(r.when) })));
  };
  const [banner, setBanner] = useState<string | null>(null);
  const [trial, setTrial] = useState<IntakeDryRunResult | null>(null);
  const saving = create.isPending || update.isPending;

  const built = (): TierRule[] =>
    rules.map((r) => ({ name: r.name.trim() || 'Rule', tier: r.tier, when: conditionOf(r.tests) }));
  const problem = rules.findIndex((r) => !r.tests.length || r.tests.some((t) => (Array.isArray(t.value) ? !t.value.length : !t.value || Number.isNaN(Number(t.value))))); // prettier-ignore
  const body = () => ({ name: name.trim(), heading: heading.trim(), intro: intro.trim(), brand_name: brand.trim(), accent, active, placement, default_tier: def, rules: built(), journeys: Object.fromEntries(INTAKE_TIERS.map((t) => [t, journeys[t]?.trim() || null])) }); // prettier-ignore
  const save = async () => {
    if (problem >= 0) return setBanner(`Rule ${problem + 1}: every test needs a value`);
    setBanner(null);
    try {
      if (link) await update.mutateAsync({ id: link.id, revision: link.revision, ...body() });
      else await create.mutateAsync(body());
      toast(`${name.trim()} saved.`);
      onClose();
    } catch (e) {
      setBanner(e instanceof ApiError ? e.message : 'Could not save');
    }
  };
  const tryRules = async () => {
    if (!link || problem >= 0)
      return setBanner(problem >= 0 ? `Rule ${problem + 1}: every test needs a value` : null);
    try {
      setTrial(await dryRun(link.id, built(), def));
    } catch (e) {
      setBanner(e instanceof ApiError ? e.message : 'Could not try the rules');
    }
  };
  const setRule = (i: number, p: Partial<(typeof rules)[number]>) =>
    setRules((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const setTest = (i: number, k: number, t: Test) =>
    setRule(i, { tests: rules[i]!.tests.map((x, j) => (j === k ? t : x)) });
  const move = (i: number, by: -1 | 1) => setRules((rs) => { const n = [...rs]; [n[i], n[i + by]] = [n[i + by]!, n[i]!]; return n; }); // prettier-ignore

  return (
    <Drawer
      title={link ? `Edit ${link.name}` : 'New submission link'}
      subtitle="The form businesses see, and how deep each one’s check goes."
      onClose={onClose}
      footer={<>{link && <button type="button" className="btn" onClick={tryRules}>Try the rules</button>}<span className="grow" /><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving && <Loader2 size={15} className="spin" />}{link ? 'Save changes' : 'Create link'}</button></>} // prettier-ignore
    >
      {banner && <div className="result bad" role="alert"><span>{banner}</span></div>}
      {!link && (
        <div className="field">
          <span className="field-label">Start from</span>
          <div className="link-templates" role="group" aria-label="Start from">
            {(Object.keys(LINK_TEMPLATES) as LinkTemplate[]).map((t) => (
              <button key={t} type="button" className={`link-template${template === t ? ' on' : ''}`} aria-pressed={template === t} onClick={() => applyTemplate(t)}>
                <b>{LINK_TEMPLATES[t].label}{t === 'standard_sme' && <span className="pill ok">Recommended</span>}</b>
                <span className="muted small">{LINK_TEMPLATES[t].blurb}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="two">
        <Field label="Name, for you" id="ln-name"><input id="ln-name" className="input" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Shown as" id="ln-brand"><input id="ln-brand" className="input" maxLength={120} value={brand} onChange={(e) => setBrand(e.target.value)} /></Field>
      </div>
      <Field label="Heading" id="ln-heading"><input id="ln-heading" className="input" maxLength={120} value={heading} onChange={(e) => setHeading(e.target.value)} /></Field>
      <Field label="Introduction" id="ln-intro"><textarea id="ln-intro" className="input" rows={2} maxLength={1000} value={intro} onChange={(e) => setIntro(e.target.value)} /></Field>
      <div className="two">
        <Field label="Colour" id="ln-accent"><input id="ln-accent" className="input" type="color" value={accent} onChange={(e) => setAccent(e.target.value)} /></Field>
        <Field label="Taking submissions" id="ln-active"><select id="ln-active" className="input" value={active ? 'yes' : 'no'} onChange={(e) => setActive(e.target.value === 'yes')}><option value="yes">Yes</option><option value="no">No, turned off</option></select></Field>
      </div>
      <label className="toggle-row">
        <input type="checkbox" checked={placement} onChange={(e) => setPlacement(e.target.checked)} />
        <span>Prepare insurers’ proposal forms: the form also asks revenue, revenue from abroad and higher-risk activities, and accepting chooses the forms the client fits</span>
      </label>

      <details className="rules-fold" open={rulesOpen} onToggle={(e) => setRulesOpen(e.currentTarget.open)}>
      <summary className="small">
        <b>Tier rules: {rules.length ? rules.map((r) => `${r.name.trim() || 'Rule'} → ${tierShort(r.tier).toLowerCase()}`).join('; ') : 'none'}</b>
        {!link && <span className="muted"> · Customise</span>}
      </summary>
      <p className="muted small">The first rule that matches picks the check.</p>
      {rules.map((r, i) => (
        <section key={i} className="rule-box" aria-label={`Rule ${i + 1}`}>
          <div className="rule-head">
            <input className="input grow" aria-label={`Rule ${i + 1} name`} maxLength={80} value={r.name} placeholder="e.g. Larger businesses" onChange={(e) => setRule(i, { name: e.target.value })} />
            <select className="input" aria-label={`Rule ${i + 1} check`} value={r.tier} onChange={(e) => setRule(i, { tier: e.target.value as IntakeTier })}>{INTAKE_TIERS.map((t) => <option key={t} value={t}>{tierShort(t)}</option>)}</select>
            <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label={`Move rule ${i + 1} up`} disabled={!i} onClick={() => move(i, -1)}><ArrowUp size={14} /></button>
            <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label={`Move rule ${i + 1} down`} disabled={i === rules.length - 1} onClick={() => move(i, 1)}><ArrowDown size={14} /></button>
            <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label={`Remove rule ${i + 1}`} onClick={() => setRules((rs) => rs.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
          </div>
          {r.tests.map((t, k) => {
            const opts = TRIAGE_FACTS[t.fact].options;
            return (
              <div key={k} className="rule-test">
                <span className="muted small">{k ? 'and' : 'when'}</span>
                <select className="input" aria-label={`Rule ${i + 1} test ${k + 1} question`} value={t.fact} onChange={(e) => setTest(i, k, newTest(e.target.value as TriageFact))}>
                  {Object.entries(TRIAGE_FACTS).map(([f, d]) => <option key={f} value={f}>{d.label}</option>)}
                </select>
                {opts ? (
                  <>
                    <select className="input" aria-label={`Rule ${i + 1} test ${k + 1} comparison`} value={t.op} onChange={(e) => setTest(i, k, { ...t, op: e.target.value as Test['op'] })}><option value="in">is one of</option><option value="not_in">is not one of</option></select>
                    <span className="rule-values" role="group" aria-label={`Rule ${i + 1} test ${k + 1} values`}>
                      {opts.map(([v, l]) => (
                        <label key={v} className="small"><input type="checkbox" checked={(t.value as string[]).includes(v)} onChange={(e) => setTest(i, k, { ...t, value: e.target.checked ? [...(t.value as string[]), v] : (t.value as string[]).filter((x) => x !== v) })} /> {l}</label>
                      ))}
                    </span>
                  </>
                ) : (
                  <>
                    <select className="input" aria-label={`Rule ${i + 1} test ${k + 1} comparison`} value={t.op} onChange={(e) => setTest(i, k, { ...t, op: e.target.value as Test['op'] })}><option value="gte">is at least</option><option value="lt">is under</option></select>
                    <input className="input" aria-label={`Rule ${i + 1} test ${k + 1} value`} inputMode="decimal" value={t.value as string} onChange={(e) => setTest(i, k, { ...t, value: e.target.value })} />
                  </>
                )}
                <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label={`Remove test ${k + 1} of rule ${i + 1}`} disabled={r.tests.length === 1} onClick={() => setRule(i, { tests: r.tests.filter((_, j) => j !== k) })}><Trash2 size={13} /></button>
              </div>
            );
          })}
          <button type="button" className="linklike left small" onClick={() => setRule(i, { tests: [...r.tests, newTest()] })}>+ and another test</button>
        </section>
      ))}
      <button type="button" className="btn btn-sm" disabled={rules.length >= 20} onClick={() => setRules((rs) => [...rs, { name: '', tier: 'full', tests: [newTest()] }])}><Plus size={14} /> Add a rule</button>
      <Field label="When no rule matches" id="ln-default"><select id="ln-default" className="input" value={def} onChange={(e) => setDef(e.target.value as IntakeTier)}>{INTAKE_TIERS.map((t) => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}</select></Field>
      </details>

      <details>
        <summary className="small">Journey for each check</summary>
        <p className="muted small">The journey key from Studio each check follows. Leave empty to use your published journey.</p>
        {INTAKE_TIERS.map((t) => (
          <Field key={t} label={tierShort(t)} id={`ln-j-${t}`}><input id={`ln-j-${t}`} className="input mono" maxLength={64} placeholder="e.g. JRN-SME-MFG-MY" value={journeys[t] ?? ''} onChange={(e) => setJourneys((j) => ({ ...j, [t]: e.target.value }))} /></Field>
        ))}
      </details>

      {trial && (
        <section className="dry-run" aria-label="Rules tried">
          <div className="eyebrow">Tried on {trial.source === 'submissions' ? 'this link’s recent submissions' : 'sample businesses'}: {INTAKE_TIERS.map((t) => `${trial.counts[t]} ${tierShort(t).toLowerCase()}`).join(', ')}</div>
          <ul className="plain-list small">
            {trial.rows.map((r, i) => (
              <li key={i} className="row-between"><span>{r.label}</span><span>{tierShort(r.tier)}{r.rule ? <span className="muted"> · {r.rule}</span> : <span className="muted"> · default</span>}{r.was && r.was !== r.tier && <span className="pill warn"> was {tierShort(r.was).toLowerCase()}</span>}</span></li>
            ))}
          </ul>
        </section>
      )}
    </Drawer>
  ); // prettier-ignore
}

function Field({ label, id, children }: { label: string; id: string; children: ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}
